import React, {useCallback, useEffect, useState, useRef} from 'react';
import {FeaturesGridCard, FeaturesGridCardProps} from '../../elements/features/FeaturesGridCard';
import {GridContainer, CenteredGrid} from '../../elements/grid/GridContainer';
import Grid from '@material-ui/core/Grid';
import styled from 'styled-components';
import {AnimatedHeading, OutlinedCard, OutlinedCardProps, VerticalSpacer} from '../../elements';
import {AccentColorOptions, useBreakpointDown, useBreakpointUp} from 'epic-ue-theme';
import throttle from 'lodash.throttle';
import root from 'window-or-global';
import {AnimateOnViewWrap} from '../../wraps/animation/AnimateOnViewWrap';
import classNames from 'classnames';

const topOffset = 84; // Subnav Height + 32

const Styled = styled.div`
    .scroll-area {
        position: relative;
    }
    .outlined-card-wrap {
        opacity: 1;
        max-width: 100%;
        &.fixed {
            position: fixed;
            top: ${topOffset}px;
        }
        &.past {
            position: absolute;
            bottom: 0;
        }
    }

    .ue-outlined-card {
        ${(p) => p.theme.breakpoints.down('sm')} {
            margin: 0 auto;
        }
    }
`;

export interface FeaturesGridProps {
    leftColumnCards?: FeaturesGridCardProps[];
    rightColumnCards?: FeaturesGridCardProps[];
    heading?: string;
    accentColor?: AccentColorOptions;
    outlinedCard?: OutlinedCardProps;
    featuresIconAccentColor?: AccentColorOptions;
}

const shouldBeSticky = (el, outlinedCardWrap) => {
    let cardWrapHeight = 0;
    let sticky = false;
    let reachedTopEdge = false;
    let scrolledPast = false;
    if (outlinedCardWrap) {
        const {height} = outlinedCardWrap.getBoundingClientRect();
        cardWrapHeight = height;
        const rect = el.getBoundingClientRect();
        reachedTopEdge = rect.top - topOffset < 0;
        scrolledPast = rect.bottom - cardWrapHeight - 100 < 0;
        sticky = reachedTopEdge && !scrolledPast;
    }
    return {sticky, reachedTopEdge, scrolledPast};
};

export const FeaturesGrid = ({
    heading = '',
    leftColumnCards = [],
    rightColumnCards = [],
    accentColor,
    outlinedCard = {},
    featuresIconAccentColor
}: FeaturesGridProps): JSX.Element => {
    const lgUp = useBreakpointUp('lg');
    const mdUp = useBreakpointUp('md');
    const [isSticky, setIsSticky] = useState(false);
    const [isScrolledPast, setIsScrolledPast] = useState(false);
    const [outlinedCardWidth, setOutlinedCardWidth] = useState('auto');
    const scrollAreaEl = useRef(null);
    const isMdDown = useBreakpointDown('md');
    const isLgUp = useBreakpointUp('lg');

    const trackScrolling = useCallback(
        throttle(() => {
            if (isMdDown) {
                setIsSticky(false);
                setIsScrolledPast(false);
                return;
            }

            /**
             * Determine if the outlined card should be in the sticky or static state
             */
            if (scrollAreaEl && scrollAreaEl.current) {
                const outlinedCardWrap = root.document.getElementById('outlinedCardWrap');
                const {sticky, scrolledPast} = shouldBeSticky(
                    scrollAreaEl.current,
                    outlinedCardWrap
                );
                setIsSticky(sticky);
                setIsScrolledPast(scrolledPast);

                if (sticky) {
                    if (outlinedCardWrap) {
                        const {width} = outlinedCardWrap.getBoundingClientRect();
                        setOutlinedCardWidth(width);
                    }
                }
            }
        }, 25),
        [
            isSticky,
            setIsSticky,
            scrollAreaEl,
            isMdDown,
            outlinedCardWidth,
            setOutlinedCardWidth,
            setIsScrolledPast
        ]
    );

    /**
     * After first render add a throttled scroll listener
     */
    useEffect(() => {
        root.document.addEventListener('scroll', trackScrolling);
        root.document.addEventListener('resize', trackScrolling);
        return () => {
            root.document.removeEventListener('scroll', trackScrolling);
            root.document.removeEventListener('resize', trackScrolling);
        };
    }, [trackScrolling]);

    const enableScrollClasses = isLgUp;

    return (
        <Styled>
            <GridContainer>
                <CenteredGrid>
                    <Grid item xs={12} md={9} lg={6}>
                        <AnimatedHeading heading={heading} />
                    </Grid>
                </CenteredGrid>
                <VerticalSpacer variant="custom" spacing={12} downSmSpacing={4} />

                <div className="scroll-area" ref={scrollAreaEl}>
                    <CenteredGrid alignItems="flex-start">
                        <Grid item xs={12} lg={4}>
                            <CenteredGrid alignItems="flex-start">
                                <Grid item xs={12} md={10} style={{paddingRight: 0}}>
                                    <div
                                        id="outlinedCardWrap"
                                        style={{
                                            width: outlinedCardWidth
                                        }}
                                        className={classNames('outlined-card-wrap', {
                                            fixed: enableScrollClasses && isSticky,
                                            past: enableScrollClasses && isScrolledPast
                                        })}>
                                        <AnimateOnViewWrap translateY={30} delay={0.25}>
                                            <OutlinedCard
                                                {...outlinedCard}
                                                accentColor={accentColor}
                                            />
                                        </AnimateOnViewWrap>
                                    </div>
                                </Grid>
                                {lgUp ? <Grid item lg={2} /> : ''}
                            </CenteredGrid>
                            <VerticalSpacer variant="layout" />
                        </Grid>
                        <Grid item xs={12} lg={8}>
                            <CenteredGrid alignItems="flex-start">
                                <Grid item xs={12} md={5}>
                                    {leftColumnCards.map((card, i) => (
                                        <React.Fragment key={card.title}>
                                            <AnimateOnViewWrap translateY={60} delay={0.5}>
                                                <FeaturesGridCard
                                                    {...card}
                                                    accentColor={accentColor}
                                                    featuresIconAccentColor={
                                                        featuresIconAccentColor
                                                    }
                                                />
                                            </AnimateOnViewWrap>
                                            {i !== leftColumnCards.length - 1 ? (
                                                <VerticalSpacer
                                                    variant="custom"
                                                    spacing={16}
                                                    downSmSpacing={7}
                                                />
                                            ) : (
                                                ''
                                            )}
                                        </React.Fragment>
                                    ))}
                                </Grid>
                                {mdUp ? <Grid item md={2} /> : ''}
                                <Grid item xs={12} md={5}>
                                    {mdUp ? (
                                        <>
                                            <VerticalSpacer variant="layout" />
                                            <VerticalSpacer variant="layout" />
                                        </>
                                    ) : (
                                        <VerticalSpacer variant="custom" downSmSpacing={7} />
                                    )}
                                    {rightColumnCards.map((card, i) => (
                                        <React.Fragment key={card.title}>
                                            <AnimateOnViewWrap translateY={60} delay={0.5}>
                                                <FeaturesGridCard
                                                    {...card}
                                                    accentColor={accentColor}
                                                    featuresIconAccentColor={
                                                        featuresIconAccentColor
                                                    }
                                                />
                                            </AnimateOnViewWrap>
                                            {i !== rightColumnCards.length - 1 ? (
                                                <VerticalSpacer
                                                    variant="custom"
                                                    spacing={16}
                                                    downSmSpacing={7}
                                                />
                                            ) : (
                                                ''
                                            )}
                                        </React.Fragment>
                                    ))}
                                </Grid>
                            </CenteredGrid>
                        </Grid>
                    </CenteredGrid>
                </div>
            </GridContainer>
        </Styled>
    );
};
